import type { AxiosInstance } from 'axios';
import { TokenRefreshAuthError, TokenRefreshNetworkError } from '../errors';
import type { AuthFailureDetails } from '../types/auth';
import type { ClientState } from '../types/state';
import { processQueue } from '../queue/processor';
import type { RefreshAccessTokenDependencies } from './refresh';

/**
 * Dependencies for token refresh handler.
 */
export interface HandleTokenRefreshDependencies
  extends RefreshAccessTokenDependencies {
  /** Client state containing refresh promise and queues */
  state: ClientState;

  /** Axios instance for processing queued requests */
  authenticatedInstance: AxiosInstance;

  /** Callback for auth failures (logout) */
  onAuthFailure: (details: AuthFailureDetails) => void;

  /** Function to refresh the access token */
  refreshAccessToken: () => Promise<string>;
}

/**
 * Handle token refresh with queue management and error handling.
 *
 * This function orchestrates the token refresh process:
 * 1. Prevents concurrent refresh attempts (returns existing promise if in progress)
 * 2. Calls refreshAccessToken() to get new token
 * 3. Processes queued requests on success
 * 4. Routes errors to appropriate handlers (auth failure vs network error)
 * 5. Cleans up state when complete
 *
 * Error Routing:
 * - TokenRefreshAuthError → calls onAuthFailure, processes queue with error
 * - TokenRefreshNetworkError → calls onNetworkError (if not already shown)
 * - Unknown errors → treated as network errors (user stays logged in)
 *
 * @param deps Dependencies (state, instances, handlers)
 * @returns Promise resolving to the new access token
 * @throws TokenRefreshAuthError for auth failures
 * @throws TokenRefreshNetworkError for network failures
 * @throws Error for unknown failures
 *
 * @example
 * ```typescript
 * try {
 *   const newToken = await handleTokenRefresh(deps);
 *   // Token refreshed, queued requests processed
 * } catch (error) {
 *   // Error handled by onAuthFailure or onNetworkError callbacks
 * }
 * ```
 */
export async function handleTokenRefresh(
  deps: HandleTokenRefreshDependencies
): Promise<string> {
  const {
    state,
    authenticatedInstance,
    onAuthFailure,
    onNetworkError,
    retryQueuedRequests,
    refreshAccessToken,
  } = deps;

  // If refresh is already in progress, return the existing promise
  if (state.refreshPromise) {
    return state.refreshPromise;
  }

  // Create new refresh promise
  state.refreshPromise = refreshAccessToken()
    .then((newToken) => {
      // Process queued requests with new token
      processQueue(state, authenticatedInstance, null, newToken);
      return newToken;
    })
    .catch((error) => {
      if (error instanceof TokenRefreshAuthError) {
        // Genuine auth failure - logout user
        processQueue(state, authenticatedInstance, error);
        onAuthFailure({
          error: error.message,
          refreshFailed: true,
          networkError: false,
          timestamp: new Date().toISOString(),
        });
        throw error;
      } else if (error instanceof TokenRefreshNetworkError) {
        // Infrastructure failure - hold queue and show retry UI
        console.error(
          "🚨 Network error during token refresh - user stays logged in"
        );

        // Dispatch event for UI to show retry dialog/snackbar
        // (only if not already shown during retry loop)
        if (!error.uiAlreadyShown) {
          onNetworkError(error.message, retryQueuedRequests, error.isColdStart);
        }

        throw error;
      } else {
        // Unknown error - treat as network error for safety
        // Keep queue intact for manual retry via UI
        console.error(
          "🚨 Unknown error during token refresh - user stays logged in"
        );
        onNetworkError(
          "An unexpected error occurred. Please try again.",
          retryQueuedRequests,
          false // Unknown errors are not cold starts
        );
        throw error;
      }
    })
    .finally(() => {
      // Clear refresh promise
      state.refreshPromise = null;
    });

  return state.refreshPromise;
}
